import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAPI } from '../portal-apis';

const ThemeContext = createContext();

export const DEFAULT_APP_ID = '98f40ef1-617a-4152-ab48-c0d4f3f14552';

// Get app version for cache busting
const APP_VERSION = import.meta.env.VITE_VERSION || '0.2.0';

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    app_id: DEFAULT_APP_ID,
    colors: {
      primary: '#f67d20ff',
      secondary: '#5f4ab4ff',
      text: {
        primary: 'rgba(255, 255, 255, 0.85)',
        secondary: 'rgba(255, 255, 255, 0.65)',
        heading: 'rgba(255, 255, 255, 0.90)',
      },
      background: {
        primary: '#0A0A0A',
        secondary: '#22242a',
        modal: '#000',
        overlay: 'rgba(22, 24, 31, 0.8)',
        hover: '#2c2c2f',
        selected: '#2a2a2a',
      },
      surface: {
        level2: '#1A1A1A',
        level3: '#2A2A2A',
        level4: '#3A3A3A',
        focused: '#3D3C41',
        quiet: '#22242a',
      },
      border: '#d9d9d9',
      error: '#f5222d',
      warning: {
        main: '#faad14',
      },
      link: '#2d93ecff',
      highlight: '#ff4d4f',
      success: '#52c41a',
      disabled: 'rgba(255, 255, 255, 0.25)',
    },
    logo: {
      primary: 'https://hotmic-fast.b-cdn.net/1736966897899-nstl9s.svg',
      favicon: '/favicon.ico',
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const updateFavicon = themeData => {
      const favicon = document.querySelector("link[rel='icon']");
      if (favicon && themeData?.logo?.favicon) {
        favicon.href = themeData.logo.favicon;
      }
    };

    const loadTheme = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0];
        const cacheKey = `theme_${subdomain}_v${APP_VERSION}`;

        // Check localStorage first
        const cachedTheme = localStorage.getItem(cacheKey);
        if (cachedTheme) {
          const parsedTheme = JSON.parse(cachedTheme);
          setTheme(parsedTheme);
          updateFavicon(parsedTheme);
          setLoading(false);
          return;
        }

        // Fetch from API
        const loadedTheme = await adminAPI.getThemeBySubdomain(subdomain);

        // Validate that we got a valid theme object
        if (!loadedTheme || !loadedTheme.app_id) {
          throw new Error('Invalid theme response');
        }

        localStorage.setItem(cacheKey, JSON.stringify(loadedTheme));
        setTheme(loadedTheme);
        updateFavicon(loadedTheme);
      } catch (error) {
        console.error('Error loading theme:', error);

        // Fallback to app theme (HotMic)
        const subdomain = window.location.hostname.split('.')[0];
        if (subdomain !== 'app') {
          try {
            const fallbackTheme = await adminAPI.getThemeBySubdomain('app');
            const cacheKey = `theme_${subdomain}_v${APP_VERSION}`;
            localStorage.setItem(cacheKey, JSON.stringify(fallbackTheme));
            setTheme(fallbackTheme);
            updateFavicon(fallbackTheme);
          } catch (fallbackError) {
            console.error('Error loading fallback theme:', fallbackError);
            // Initial state already has full HotMic theme — don't overwrite with skeleton
          }
        } else {
          // Initial state already has full HotMic theme — don't overwrite with skeleton
        }
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, []);

  const value = {
    theme,
    setTheme,
    loading,
    app_id: theme?.app_id || DEFAULT_APP_ID,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const useAppId = () => {
  const { app_id } = useTheme();
  return app_id;
};

// Utility function for manual theme cache busting
export const clearAllThemeCaches = () => {
  const allKeys = Object.keys(localStorage);
  const themeCacheKeys = allKeys.filter(key => key.startsWith('hotmic_theme_cache') || key.startsWith('theme_'));

  themeCacheKeys.forEach(key => localStorage.removeItem(key));

  return themeCacheKeys.length;
};

// Utility function to check if there are stale theme caches
export const getStaleThemeCacheCount = () => {
  const allKeys = Object.keys(localStorage);
  const currentVersion = import.meta.env.VITE_VERSION || '0.2.0';

  const staleKeys = allKeys.filter(key => {
    if (key.startsWith('hotmic_theme_cache_v') && !key.endsWith(`_v${currentVersion}`)) {
      return true;
    }
    if (key.startsWith('theme_') && !key.includes(`_v${currentVersion}`)) {
      return true;
    }
    return false;
  });

  return staleKeys.length;
};
